import moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../store';
import CountDownTimer from '../common/countDownTimer';
import { cancelReservation, getReservationDetailsOptionalPatient } from '../../actions/appointmentActions';
import { availabilitySearch as routeToAvailabilitySearch } from '../../routes';
import LoadingIndicator from '../loadingIndicator';
import { PhoneNumber } from '../common/phoneNumber';
import InfoPanelHeader from '../common/infoPanelHeader';
import {
	displayFullName,
	displayProviderNameWithProfessionalDesignation,
} from '../../lib/standardLibrary';
import '../../features/patient/css/patientInfoTabbedCollapsable.css';
import './slotDetailsPanel.css';

const SlotDetailsPanel = () => {
	const dispatch = useAppDispatch();
	const reservation = useAppSelector((state: any) => state.appointment.reservation);
	const reservationDetails = useAppSelector((state: any) => state.appointment.reservationDetails);
	const decisionSupport = useAppSelector((state: any) => state.decisionSupport);
	const providerFieldConfig = useAppSelector((state: any) => state.config.provider);
	const routePrefix = useAppSelector((state: any) => state.config.instance.routePrefix);
	const token = useAppSelector((state: any) => state.auth.token);
	const [isReservationExpired, setIsReservationExpired] = useState(false);
	const [isChangingAppointment, setIsChangingAppointment] = useState(false);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);
	const [detailsError, setDetailsError] = useState('');

	const serviceSiteId = reservation?.timeSlotRequestInfo?.serviceSiteId;
	const insuranceProviderId = decisionSupport?.availabilitySearchCriteria?.insuranceProviderId;
	const payorTypeId = decisionSupport?.availabilitySearchCriteria?.payorTypeId;

	useEffect(() => {
		if (!reservation?.flexCalendarEntryId || !serviceSiteId || reservationDetails?.providerAppointmentDateTime) {
			return;
		}

		setIsLoadingDetails(true);
		setDetailsError('');
		dispatch(
			getReservationDetailsOptionalPatient(
				reservation.flexCalendarEntryId,
				serviceSiteId,
				insuranceProviderId ?? null,
				payorTypeId ?? null,
			),
		)
			.then((response: any) => {
				if (response?.error) {
					setDetailsError('Unable to load appointment details for the reserved slot.');
				}
			})
			.catch(() => {
				setDetailsError('Unable to load appointment details for the reserved slot.');
			})
			.finally(() => {
				setIsLoadingDetails(false);
			});
	}, [
		dispatch,
		reservation?.flexCalendarEntryId,
		serviceSiteId,
		insuranceProviderId,
		payorTypeId,
		reservationDetails?.providerAppointmentDateTime,
	]);

	if (!reservation?.reservationId) {
		return null;
	}

	const changeAppointmentClick = () => {
		setIsChangingAppointment(true);
		dispatch(cancelReservation(token, reservation.reservationId))
			.then((response: any) => {
				if (response?.error) {
					setIsChangingAppointment(false);
				} else {
					setIsChangingAppointment(false);
					dispatch(routeToAvailabilitySearch(routePrefix));
				}
			})
			.catch(() => {
				setIsChangingAppointment(false);
			});
	};

	const providerFullName = displayFullName(
		reservationDetails?.providerFirstName,
		reservationDetails?.providerLastName,
	);
	const providerDisplayName = displayProviderNameWithProfessionalDesignation(
		providerFullName,
		reservationDetails?.providerProfessionalDesignation,
	);
	const appointmentDateTime = reservationDetails?.providerAppointmentDateTime
		? moment(reservationDetails.providerAppointmentDateTime).format('dddd, M/D/YYYY h:mm a')
		: '';
	const cityStateZip = [
		[reservationDetails?.providerCity, reservationDetails?.providerState].filter(Boolean).join(', '),
		reservationDetails?.providerZip,
	]
		.filter(Boolean)
		.join(' ');
	const providerAddress = [
		reservationDetails?.providerAddressLine1,
		reservationDetails?.providerAddressLine2,
		cityStateZip,
	]
		.filter((line) => line && String(line).trim() !== '')
		.join(', ');

	const headerSegments = providerDisplayName ? [providerDisplayName] : undefined;

	return (
		<Card className="PatientInfoCardActive slot-details-panel">
			<InfoPanelHeader
				title={appointmentDateTime || 'Reserved appointment'}
				segments={headerSegments}
				trailing={
					<div className="reservationCountDown">
						{!isReservationExpired ? (
							<>
								Reservation expires in{' '}
								<CountDownTimer
									countDownExpirationTime={moment
										.utc(reservation.reservationExpirationTimeString)
										.local()
										.format()}
									countDownExpired={() => setIsReservationExpired(true)}
								/>
							</>
						) : (
							<>
								Reservation expired.
								{!isChangingAppointment ? (
									<span className="link" onClick={changeAppointmentClick}>
										{' '}
										Pick a slot again.
									</span>
								) : (
									<span> Please wait...</span>
								)}
							</>
						)}
					</div>
				}
			/>
			<CardBody className="PatientInfoCardBody">
				{detailsError && <div className="text-danger mb-2">{detailsError}</div>}
				{isLoadingDetails ? (
					<LoadingIndicator loadingMessage="Loading appointment details..." />
				) : (
					reservationDetails?.providerAppointmentDateTime && (
						<>
							<div className="slot-details-lines">
								{reservationDetails.providerSpecialty && (
									<div>{reservationDetails.providerSpecialty}</div>
								)}
								{reservationDetails.providerSiteName && (
									<div>{reservationDetails.providerSiteName}</div>
								)}
								{!reservationDetails.suppressAddress && providerAddress && (
									<div>{providerAddress}</div>
								)}
								{!reservationDetails.suppressPhoneNumber &&
									providerFieldConfig.phone?.isVisible && (
										<div>
											<PhoneNumber
												value={reservationDetails.providerPhoneNumber?.trim()}
											/>
										</div>
									)}
							</div>
							<Row>
								<Col>
									<Button
										color="secondary"
										onClick={changeAppointmentClick}
										disabled={isChangingAppointment}
									>
										Change Appointment
									</Button>
								</Col>
							</Row>
						</>
					)
				)}
			</CardBody>
		</Card>
	);
};

export default SlotDetailsPanel;
